import { AxiosError } from "axios";
import { api } from "@/shared/api/client";
import { tokenStore } from "@/shared/api/tokenStore";
import type { Paginated } from "@/features/customers/types";
import type {
  ImportPreview,
  ImportResult,
  ManualClient,
  Merchant,
  AgreementImportPreview,
  AgreementImportResult,
  AgreementCommitCreatedItem,
  AddAgreementPayload,
  AgreementRowInput,
  ImportedAgreementDetail,
  AgreementDetailUpdatePayload,
  ProductOption,
  CatalogueBrand,
  CatalogueModel,
  CatalogueBrandCreateResult,
  CatalogueModelCreateResult,
  ColorOption,
  ImportHistoryEntry,
} from "./types";
import {
  mockGetMerchants,
  mockValidate,
  mockCommit,
  mockAddClient,
  mockValidateAgreement,
  mockCommitAgreement,
  mockValidateAgreementRows,
  mockCommitAgreementRows,
  mockGetImportedAgreement,
  mockUpdateImportedAgreement,
  mockGetProductOptions,
  mockGetBrands,
  mockGetBrandModels,
  mockCreateBrand,
  mockCreateModel,
  mockGetColors,
  mockAddAgreement,
} from "./mock";

// ── Mock toggle ──────────────────────────────────────────────────────────────
// Same convention as the customers/settings features. FLIP TO REAL: set
// VITE_USE_MOCK=false — no other change needed; hooks/components speak the same shape.
const USE_MOCK =
  (import.meta.env.VITE_USE_MOCK ?? "true").toString().toLowerCase() !== "false";

// Small latency so loading states are exercised in mock mode.
const delay = (ms = 300) => new Promise<void>((r) => setTimeout(r, ms));

// ── Error mapping boundary ───────────────────────────────────────────────────
// The add-client endpoint returns DRF field-level 400 errors:
//   phone_number: ["invalid_phone"] | ["phone_exists"]
//   pin:          ["invalid_pin"]   | ["pin_exists"]
//   passport_number: ["passport_exists"]
// Normalise to a stable code at this boundary so the form never branches on raw strings.
export type ImportErrorCode =
  | "phone_exists"
  | "pin_exists"
  | "passport_exists"
  | "invalid_phone"
  | "invalid_pin"
  | "generic";

export class ImportError extends Error {
  constructor(
    public code: ImportErrorCode,
    // Which field the error belongs to (for inline display). The add-agreement endpoint can also
    // surface an agreement-level error ("agreement") whose human message is carried in `detail`.
    public field?: "phone_number" | "pin" | "passport_number" | "agreement",
    // Raw backend message for the agreement-level case (shown verbatim — backend texts are localized).
    public detail?: string,
  ) {
    super(code);
    this.name = "ImportError";
  }
}

// Pulls a string out of a DRF error value that may be a string, a string[] or {non_field_errors:[]}.
function firstMessage(v: unknown): string | undefined {
  if (typeof v === "string") return v;
  if (Array.isArray(v) && typeof v[0] === "string") return v[0];
  if (v && typeof v === "object") {
    const nf = (v as Record<string, unknown>)["non_field_errors"];
    if (Array.isArray(nf) && typeof nf[0] === "string") return nf[0];
  }
  return undefined;
}

function toImportError(e: unknown): ImportError {
  // Mock path tags the thrown error with `importCode`.
  const tagged = (e as { importCode?: string }).importCode;
  if (tagged) {
    if (tagged === "phone_exists") return new ImportError("phone_exists", "phone_number");
    if (tagged === "pin_exists") return new ImportError("pin_exists", "pin");
    if (tagged === "passport_exists") return new ImportError("passport_exists", "passport_number");
    return new ImportError("generic");
  }

  // Real API path: parse DRF 400 field errors. The add-agreement endpoint nests the inline-new-client
  // errors under a `new_client` object; client-add returns them at the top level. Check both.
  if (e instanceof AxiosError && e.response?.status === 400) {
    const raw = e.response.data as Record<string, unknown> | undefined;
    if (raw) {
      const nested = (raw["new_client"] as Record<string, unknown>) ?? undefined;
      const data = (nested ?? raw) as Record<string, unknown>;

      const phone = firstMessage(data["phone_number"]);
      if (phone === "phone_exists") return new ImportError("phone_exists", "phone_number");
      if (phone === "invalid_phone") return new ImportError("invalid_phone", "phone_number");

      const pin = firstMessage(data["pin"]);
      if (pin === "pin_exists") return new ImportError("pin_exists", "pin");
      if (pin === "invalid_pin") return new ImportError("invalid_pin", "pin");

      const passport = firstMessage(data["passport_number"]);
      if (passport === "passport_exists") return new ImportError("passport_exists", "passport_number");

      // Agreement-level validation failure (per-row commit message). The add-agreement endpoint
      // returns the human reason under `agreement` (fit-guard / paid>financed / missing-field) and
      // an existing-client problem under `client_id` (e.g. "client_not_found"). Surface either one
      // verbatim inline — without these keys every business rejection collapsed to the generic toast.
      const agreementMsg =
        firstMessage(raw["agreement"]) ??
        firstMessage(raw["client_id"]) ??
        firstMessage(raw["client"]) ??
        firstMessage(raw["detail"]) ??
        firstMessage(raw["non_field_errors"]) ??
        firstMessage(raw);
      if (agreementMsg) return new ImportError("generic", "agreement", agreementMsg);
    }
  }

  return new ImportError("generic");
}

// ── API functions ────────────────────────────────────────────────────────────

export async function getMerchants(search?: string): Promise<Paginated<Merchant>> {
  if (USE_MOCK) {
    await delay();
    return mockGetMerchants(search);
  }
  const res = await api.get<Paginated<Merchant>>("/support/merchants/", {
    params: search ? { search } : undefined,
  });
  return res.data;
}

export async function validateClientImport(
  merchantId: number,
  file: File,
): Promise<ImportPreview> {
  if (USE_MOCK) {
    await delay(600);
    return mockValidate();
  }
  const form = new FormData();
  form.append("file", file);
  // Do NOT set Content-Type — browser sets multipart/form-data with boundary automatically.
  const res = await api.post<ImportPreview>(
    `/support/merchants/${merchantId}/import-clients/`,
    form,
    { params: { dry_run: "true" } },
  );
  return res.data;
}

export async function commitClientImport(
  merchantId: number,
  file: File,
): Promise<ImportResult> {
  if (USE_MOCK) {
    await delay(600);
    return mockCommit();
  }
  const form = new FormData();
  form.append("file", file);
  const res = await api.post<ImportResult>(
    `/support/merchants/${merchantId}/import-clients/`,
    form,
    { params: { dry_run: "false" } },
  );
  return res.data;
}

export async function addClient(
  merchantId: number,
  payload: ManualClient,
): Promise<{ id: number; first_name: string; last_name: string; phone_number: string }> {
  if (USE_MOCK) {
    await delay();
    try {
      return mockAddClient(merchantId, payload);
    } catch (err) {
      throw toImportError(err);
    }
  }
  try {
    const res = await api.post<{
      id: number;
      first_name: string;
      last_name: string;
      phone_number: string;
    }>(`/support/merchants/${merchantId}/add-client/`, payload);
    return res.data;
  } catch (err) {
    throw toImportError(err);
  }
}

/**
 * POST /support/merchants/{id}/add-agreement/ (0119) — create a single agreement manually.
 * The body carries the customer (existing client_id XOR inline new_client) plus the agreement fields;
 * the backend resolves/creates the customer and runs the SAME validated commit path as the import
 * rows-path (atomic). Returns the created loan as an AgreementCommitCreatedItem.
 * Errors are normalised to ImportError at this boundary (inline-new-client field errors + agreement
 * validation messages) exactly like addClient, so the drawer never branches on raw strings.
 *
 * notification_before: the caller (AddDrawer) converts the raw form string to a number before
 * building the payload — blank/empty string → omit the field entirely (undefined is not serialized),
 * so the backend receives null-equivalent (field not present). Never send 0 or an empty string.
 */
export async function addAgreement(
  merchantId: number,
  payload: AddAgreementPayload,
): Promise<AgreementCommitCreatedItem> {
  if (USE_MOCK) {
    await delay();
    try {
      return mockAddAgreement(merchantId, payload);
    } catch (err) {
      throw toImportError(err);
    }
  }
  try {
    const res = await api.post<AgreementCommitCreatedItem>(
      `/support/merchants/${merchantId}/add-agreement/`,
      payload,
    );
    return res.data;
  } catch (err) {
    throw toImportError(err);
  }
}

// Downloads the Excel template and triggers a browser save-as dialog.
export async function downloadTemplate(): Promise<void> {
  if (USE_MOCK) {
    await delay(200);
    // Create a minimal blob so the download still fires in mock mode.
    const blob = new Blob(["mock template"], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    triggerDownload(blob, "clients_template.xlsx");
    return;
  }
  const res = await api.get<Blob>("/support/merchants/import-clients-template/", {
    responseType: "blob",
  });
  triggerDownload(res.data, "clients_template.xlsx");
}

// ── Agreement import API ─────────────────────────────────────────────────────

export async function validateAgreementImport(
  merchantId: number,
  file: File,
): Promise<AgreementImportPreview> {
  if (USE_MOCK) {
    await delay(600);
    return mockValidateAgreement();
  }
  const form = new FormData();
  form.append("file", file);
  // Do NOT set Content-Type — browser sets multipart/form-data with boundary automatically.
  const res = await api.post<AgreementImportPreview>(
    `/support/merchants/${merchantId}/import-agreements/`,
    form,
    { params: { dry_run: "true" } },
  );
  return res.data;
}

export async function commitAgreementImport(
  merchantId: number,
  file: File,
): Promise<AgreementImportResult> {
  if (USE_MOCK) {
    await delay(600);
    return mockCommitAgreement();
  }
  const form = new FormData();
  form.append("file", file);
  const res = await api.post<AgreementImportResult>(
    `/support/merchants/${merchantId}/import-agreements/`,
    form,
    { params: { dry_run: "false" } },
  );
  return res.data;
}

// ── Agreement import — editable rows path (subtask 06) ───────────────────────
// Re-validates / commits hand-edited rows WITHOUT re-uploading the Excel file. The body is plain
// JSON `{ rows }` (NOT FormData) so the client.ts interceptor keeps the default application/json
// header; the backend `_coerce_row` parses each row and runs the same validators as the file path.

export async function validateAgreementRows(
  merchantId: number,
  rows: AgreementRowInput[],
): Promise<AgreementImportPreview> {
  if (USE_MOCK) {
    await delay(400);
    return mockValidateAgreementRows(rows);
  }
  const res = await api.post<AgreementImportPreview>(
    `/support/merchants/${merchantId}/import-agreements/`,
    { rows },
    { params: { dry_run: "true" } },
  );
  return res.data;
}

export async function commitAgreementRows(
  merchantId: number,
  rows: AgreementRowInput[],
): Promise<AgreementImportResult> {
  if (USE_MOCK) {
    await delay(600);
    return mockCommitAgreementRows(rows);
  }
  const res = await api.post<AgreementImportResult>(
    `/support/merchants/${merchantId}/import-agreements/`,
    { rows },
    { params: { dry_run: "false" } },
  );
  return res.data;
}

// Downloads the agreements Excel template and triggers a browser save-as dialog.
export async function downloadAgreementTemplate(): Promise<void> {
  if (USE_MOCK) {
    await delay(200);
    const blob = new Blob(["mock agreements template"], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    triggerDownload(blob, "agreements_template.xlsx");
    return;
  }
  const res = await api.get<Blob>("/support/merchants/import-agreements-template/", {
    responseType: "blob",
  });
  triggerDownload(res.data, "agreements_template.xlsx");
}

// ── Agreement detail / edit API ───────────────────────────────────────────────

/** GET /support/agreements/{loanId}/ — fetch full detail for the edit drawer. */
export async function getImportedAgreement(loanId: number): Promise<ImportedAgreementDetail> {
  if (USE_MOCK) {
    await delay(400);
    return mockGetImportedAgreement(loanId);
  }
  const res = await api.get<ImportedAgreementDetail>(`/support/agreements/${loanId}/`);
  return res.data;
}

/**
 * PATCH /support/agreements/{loanId}/ — update editable detail fields.
 * Body: { additional_data: { <editable keys only> } }.
 * Returns the updated detail shape (200). 404 means not an imported agreement.
 */
export async function updateImportedAgreement(
  loanId: number,
  additional_data: Record<string, string>,
): Promise<ImportedAgreementDetail> {
  if (USE_MOCK) {
    await delay(400);
    return mockUpdateImportedAgreement(loanId, additional_data);
  }
  const payload: AgreementDetailUpdatePayload = { additional_data };
  const res = await api.patch<ImportedAgreementDetail>(`/support/agreements/${loanId}/`, payload);
  return res.data;
}

// ── Contract preview / rollback (0122) ────────────────────────────────────────

/**
 * GET /support/agreements/{loanId}/contract_preview/ — the real WeasyPrint contract PDF (stamped
 * "IMPORT QILINGAN") for a just-created imported agreement, as a Blob. Used by the add-agreement
 * drawer to show the dogovor in an <iframe> before the operator confirms.
 *
 * Throws when the PDF cannot be produced: 404 (CONTRACT_TEMPLATE_V2 flag off / not an imported loan)
 * or any network error. The caller (AgreementAddDrawer) catches this and falls back to the HTML
 * summary modal — mirrors the merchant app's `pdfFailed` path. In mock mode there is no real
 * renderer, so we always throw to exercise that same fallback.
 */
export async function fetchAgreementContractPreview(loanId: number): Promise<Blob> {
  if (USE_MOCK) {
    await delay(300);
    throw new Error("contract_preview unavailable in mock mode");
  }
  const res = await api.get<Blob>(`/support/agreements/${loanId}/contract_preview/`, {
    responseType: "blob",
  });
  return res.data;
}

/**
 * DELETE /support/agreements/{loanId}/discard/ — roll back an unconfirmed add by hard-deleting the
 * just-created Loan (the preview POSTs it to render the real PDF; cancelling must not leave it behind).
 * Audited server-side (SUPPORT_AGREEMENT_DISCARDED). No-op in mock mode.
 */
export async function discardAgreement(loanId: number): Promise<void> {
  if (USE_MOCK) {
    await delay(200);
    return;
  }
  await api.delete(`/support/agreements/${loanId}/discard/`);
}

/**
 * POST /support/agreements/{loanId}/confirm/ (0127) — finalize a hand-added agreement AFTER the
 * operator reviewed its contract PDF. Only now does the backend write the import-history "added" row
 * and fire the merchant FCM push (the add/review step deferred both). Idempotent server-side. No-op in
 * mock mode. The caller invalidates the customers + import-history queries on success.
 */
export async function confirmAgreement(loanId: number): Promise<void> {
  if (USE_MOCK) {
    await delay(200);
    return;
  }
  await api.post(`/support/agreements/${loanId}/confirm/`);
}

/**
 * Best-effort orphan cleanup for a tab close / refresh (0127). When the operator opened the PDF preview
 * (which created the Loan) but neither confirmed nor cancelled, a `beforeunload` can no longer await an
 * axios promise — the page is tearing down. `fetch(..., { keepalive: true })` lets the browser flush the
 * DELETE after unload. Auth + base URL are taken from the same places the axios client uses. Fire-and-
 * forget: never throws (a failed cleanup just leaves the unconfirmed loan, which an operator can discard
 * later). Not used in mock mode.
 */
export function discardAgreementBeacon(loanId: number): void {
  if (USE_MOCK) return;
  const baseURL = import.meta.env.VITE_API_URL ?? "/api";
  const access = tokenStore.getAccess();
  try {
    void fetch(`${baseURL}/support/agreements/${loanId}/discard/`, {
      method: "DELETE",
      keepalive: true,
      headers: access ? { Authorization: `Bearer ${access}` } : undefined,
    }).catch(() => {});
  } catch {
    // Swallow — cleanup is best-effort and must never block page teardown.
  }
}

// ── Product catalogue options ─────────────────────────────────────────────────

/**
 * GET /support/agreements/product-options/?type=car|technology
 * Returns the searchable list of canonical product names for the edit drawer dropdown.
 * Returns 400 from the backend for a bad/missing type — callers should guard with enabled.
 */
export async function getProductOptions(type: "car" | "technology"): Promise<ProductOption[]> {
  if (USE_MOCK) {
    await delay(300);
    return mockGetProductOptions(type);
  }
  const res = await api.get<ProductOption[]>("/support/agreements/product-options/", {
    params: { type },
  });
  return res.data;
}

// ── Inline catalogue-add (subtask 08) ────────────────────────────────────────
// Read/write the GLOBAL brand+model catalogue for the inline "add to catalogue" modal. Bodies are
// plain JSON (NOT FormData) so the client.ts interceptor keeps application/json. POST is idempotent +
// case-insensitive server-side (created=false on a repeat hit). `type` is car | technology only.

/** GET /support/agreements/brands/?type= — brands for the modal's step-1 select. */
export async function getBrands(type: "car" | "technology"): Promise<CatalogueBrand[]> {
  if (USE_MOCK) {
    await delay(250);
    return mockGetBrands(type);
  }
  const res = await api.get<CatalogueBrand[]>("/support/agreements/brands/", {
    params: { type },
  });
  return res.data;
}

/** GET /support/agreements/brand-models/?type=&brand_id= — models under one brand (step-2 select). */
export async function getBrandModels(
  type: "car" | "technology",
  brandId: number,
): Promise<CatalogueModel[]> {
  if (USE_MOCK) {
    await delay(250);
    return mockGetBrandModels(type, brandId);
  }
  const res = await api.get<CatalogueModel[]>("/support/agreements/brand-models/", {
    params: { type, brand_id: brandId },
  });
  return res.data;
}

/** POST /support/agreements/brands/ {type, name} — add (or resolve) a brand. */
export async function createBrand(
  type: "car" | "technology",
  name: string,
): Promise<CatalogueBrandCreateResult> {
  if (USE_MOCK) {
    await delay(300);
    return mockCreateBrand(type, name);
  }
  const res = await api.post<CatalogueBrandCreateResult>("/support/agreements/brands/", {
    type,
    name,
  });
  return res.data;
}

/** POST /support/agreements/brand-models/ {type, brand_id, name} — add (or resolve) a model. */
export async function createModel(
  type: "car" | "technology",
  brandId: number,
  name: string,
): Promise<CatalogueModelCreateResult> {
  if (USE_MOCK) {
    await delay(300);
    return mockCreateModel(type, brandId, name);
  }
  const res = await api.post<CatalogueModelCreateResult>("/support/agreements/brand-models/", {
    type,
    brand_id: brandId,
    name,
  });
  return res.data;
}

// ── Color catalogue options ───────────────────────────────────────────────────

// ── Import history ────────────────────────────────────────────────────────────

/**
 * GET /api/support/agreements/import-history/?merchant_id=<id>
 * Returns up to 50 entries ordered by -timestamp.
 * Pass merchantId to filter by one merchant; omit for all.
 */
export async function fetchImportHistory(
  merchantId?: number,
): Promise<ImportHistoryEntry[]> {
  const res = await api.get<ImportHistoryEntry[]>(
    "/support/agreements/import-history/",
    { params: merchantId !== undefined ? { merchant_id: merchantId } : undefined },
  );
  return res.data;
}

/** GET /support/agreements/colors/ — global colour list for the detail colour dropdown. */
export async function getColors(): Promise<ColorOption[]> {
  if (USE_MOCK) {
    await delay(250);
    return mockGetColors();
  }
  const res = await api.get<ColorOption[]>("/support/agreements/colors/");
  return res.data;
}

export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
